import urlsData from '../data/urls_endpoints.json';

type EndpointConfig = {
  api_base_url: string;
  endpoints: {
    payments: any;
    user: {
      register: string;
      login: string;
      login_with_google: string;
      send_verification: string;
      update: string;
      reset_password_with_code: string;
    };
    records: {
      create: string;
      edit: string;
      delete: string;
      import: string;
      join_by_code: string;
      grant_permission: string;
      find: string;
      export: string;
      get_users: string;
      remove_user: string;
    };
    transactions: {
      create: string;
      edit: string;
      delete: string;
      find: string;
    };
    transaction_entities: {
      create: string;
      edit: string;
      delete: string;
      find: string;
      find_by_cosine_similarity: string;
    };
    subscriptions: {
      subscribe: string;
      user_subscription: string;
      features: string;
      find: string;
    };
    notifications: {
      mark_as_read: string;
      all: string;
    };
    credits: {
      webhook: string;
      balance: string;
      find: string;
      flutterwave_generate_link: string;
      flutterwave_subscription_generate_link: string;
    };
    dashboard: {
      kpis: string;
    };
    accounts: {
      ledger: string;
      ledger_list: string;
      stock_overall: string;
      trading_overall: string;
      balance_sheet: string;
      pnl: string;
      trading_list: string;
      stock_list: string;
      stock_valuation: string;
      trading: string;
      assets: string;
      liabilities: string;
    };
  };
  excluded_from_cache: string[];
};

const config = urlsData as EndpointConfig;

export class UrlBuilder {
  private static baseUrl = config.api_base_url;
  
  static getFullUrl(endpoint: string, params?: Record<string, string | number>): string {
    let url = endpoint;
    
    // Replace path parameters
    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        url = url.replace(`{${key}}`, value.toString());
      });
    }
    const ret = this.baseUrl + url;
    return ret;
  }
  
  // User endpoints
  static user = {
    register: (): string => this.getFullUrl(config.endpoints.user.register),
    login: (): string => this.getFullUrl(config.endpoints.user.login),
    loginWithGoogle: (): string => this.getFullUrl(config.endpoints.user.login_with_google),
    sendVerification: (): string => this.getFullUrl(config.endpoints.user.send_verification),
    update: (): string => this.getFullUrl(config.endpoints.user.update),
    resetPasswordWithCode: (): string => this.getFullUrl(config.endpoints.user.reset_password_with_code),
  };
  
  // Records endpoints
  static records = {
    create: (): string => this.getFullUrl(config.endpoints.records.create),
    edit: (id: number): string => this.getFullUrl(config.endpoints.records.edit, { id }),
    delete: (id: number): string => this.getFullUrl(config.endpoints.records.delete, { id }),
    import: (): string => this.getFullUrl(config.endpoints.records.import),
    joinByCode: (code: string, userId: number): string => 
      this.getFullUrl(config.endpoints.records.join_by_code, { code, user_id: userId }),
    grantPermission: (userId: number): string => 
      this.getFullUrl(config.endpoints.records.grant_permission, { user_id: userId }),
    find: (): string => this.getFullUrl(config.endpoints.records.find),
    export: (id: number): string => this.getFullUrl(config.endpoints.records.export, { id }),
    getUsers: (recordId: number): string => 
      this.getFullUrl(config.endpoints.records.get_users, { record_id: recordId }),
    removeUser: (userId: number, recordId: number, targetUserId: number): string => 
      this.getFullUrl(config.endpoints.records.remove_user, { 
        user_id: userId, 
        record_id: recordId, 
        target_user: targetUserId 
      }),
  };
  
  // Transactions endpoints
  static transactions = {
    create: (userId: number): string => 
      this.getFullUrl(config.endpoints.transactions.create, { user_id: userId }),
    edit: (transactionId: number, userId: number): string =>
      this.getFullUrl(config.endpoints.transactions.edit, { transaction_id: transactionId, user_id: userId }),
    delete: (transactionId: number): string => 
      this.getFullUrl(config.endpoints.transactions.delete, { transaction_id: transactionId }),
    find: (userId: number): string => 
      this.getFullUrl(config.endpoints.transactions.find, { user_id: userId }),
  };
  
  // Transaction entities endpoints
  static transactionEntities = {
    create: (userId: number): string => 
      this.getFullUrl(config.endpoints.transaction_entities.create, { user_id: userId }),
    edit: (id: number): string => 
      this.getFullUrl(config.endpoints.transaction_entities.edit, { id }),
    delete: (id: number): string => 
      this.getFullUrl(config.endpoints.transaction_entities.delete, { id }),
    find: (userId: number): string => 
      this.getFullUrl(config.endpoints.transaction_entities.find, { user_id: userId }),
    findByCosineSimilarity: (userId: number): string => 
      this.getFullUrl(config.endpoints.transaction_entities.find_by_cosine_similarity, { user_id: userId }),
  };
  
  // Subscriptions endpoints
  static subscriptions = {
    subscribe: (): string => this.getFullUrl(config.endpoints.subscriptions.subscribe),
    userSubscription: (userId: number): string => 
      this.getFullUrl(config.endpoints.subscriptions.user_subscription, { user_id: userId }),
    features: (): string => this.getFullUrl(config.endpoints.subscriptions.features),
    find: (): string => this.getFullUrl(config.endpoints.subscriptions.find),
  };
  
  // Notifications endpoints
  static notifications = {
    markAsRead: (id: number): string => 
      this.getFullUrl(config.endpoints.notifications.mark_as_read, { id }),
    all: (userId: number): string => 
      this.getFullUrl(config.endpoints.notifications.all, { user_id: userId }),
  };
  
    // Credits endpoints
    static credits = {
      webhook: (userId: number): string => 
        this.getFullUrl(config.endpoints.credits.webhook, { user_id: userId }),
      balance: (userId: number): string => 
        this.getFullUrl(config.endpoints.credits.balance, { user_id: userId }),
      find: (userId: number): string => 
        this.getFullUrl(config.endpoints.credits.find, { user_id: userId }),
      flutterwaveGenerateLink: (): string => 
        this.getFullUrl(config.endpoints.credits.flutterwave_generate_link),
      flutterwaveSubscriptionGenerateLink: (): string => 
        this.getFullUrl(config.endpoints.credits.flutterwave_subscription_generate_link),
    };  

    // Payments endpoints
    static payments = {
      config: (): string => 
        this.getFullUrl(config.endpoints.payments.config),
    };
  
  // Dashboard endpoints
  static dashboard = {
    kpis: (userId: number): string => 
      this.getFullUrl(config.endpoints.dashboard.kpis, { user_id: userId }),
  };
  
  // Accounts endpoints
  static accounts = {
    ledger: (): string => this.getFullUrl(config.endpoints.accounts.ledger),
    ledgerList: (): string => this.getFullUrl(config.endpoints.accounts.ledger_list),
    stockOverall: (): string => this.getFullUrl(config.endpoints.accounts.stock_overall),
    tradingOverall: (): string => this.getFullUrl(config.endpoints.accounts.trading_overall),
    balanceSheet: (): string => this.getFullUrl(config.endpoints.accounts.balance_sheet),
    pnl: (): string => this.getFullUrl(config.endpoints.accounts.pnl),
    tradingList: (): string => this.getFullUrl(config.endpoints.accounts.trading_list),
    stockList: (): string => this.getFullUrl(config.endpoints.accounts.stock_list),
    stockValuation: (): string => this.getFullUrl(config.endpoints.accounts.stock_valuation),
    trading: (): string => this.getFullUrl(config.endpoints.accounts.trading),
    assets: (): string => this.getFullUrl(config.endpoints.accounts.assets),
    liabilities: (): string => this.getFullUrl(config.endpoints.accounts.liabilities),
  };
  
  // Cache exclusion check
  static isExcludedFromCache(url: string): boolean {
    return config.excluded_from_cache.some(excluded => {
      // Convert {param} to regex pattern
      const pattern = excluded.replace(/\{[^}]+\}/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(url.replace(this.baseUrl, ''));
    });
  }
}

export default UrlBuilder;